import React, { createContext, useContext, useState, useEffect } from 'react';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [companyName, setCompanyName] = useState(() => {
    return localStorage.getItem('companyName') || 'Limpa Nome';
  });

  const [companyLogo, setCompanyLogo] = useState(() => {
    return localStorage.getItem('companyLogo') || '';
  });

  const [instagramUsername, setInstagramUsername] = useState(() => {
    return localStorage.getItem('instagramUsername') || '';
  });

  const [instagramPassword, setInstagramPassword] = useState(() => {
    return localStorage.getItem('instagramPassword') || '';
  });

  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved !== null ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('companyName', companyName);
  }, [companyName]);

  useEffect(() => {
    try {
      localStorage.setItem('companyLogo', companyLogo);
    } catch (error) {
      console.error('Erro ao salvar logo no localStorage:', error);
      // Se ocorrer erro de cota, limpar o logo para evitar travamento
      if (error.name === 'QuotaExceededError') {
        localStorage.removeItem('companyLogo');
        setCompanyLogo('');
        alert('Não foi possível armazenar a logo neste navegador. Tente outra imagem ou libere espaço do navegador.');
      }
    }
  }, [companyLogo]);

  useEffect(() => {
    localStorage.setItem('instagramUsername', instagramUsername);
  }, [instagramUsername]);

  useEffect(() => {
    localStorage.setItem('instagramPassword', instagramPassword);
  }, [instagramPassword]);

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // LOGO_OTIMIZADA_5MB
  const handleLogoUpload = (file) => {
    return new Promise((resolve, reject) => {
      const TIPOS_PERMITIDOS = new Set([
        'image/png',
        'image/jpeg',
        'image/webp'
      ]);

      const LIMITE_ARQUIVO =
        5 * 1024 * 1024;

      const LIMITE_ARMAZENADO =
        1536 * 1024;

      if (!TIPOS_PERMITIDOS.has(file.type)) {
        reject(
          new Error(
            'Envie uma imagem PNG, JPEG ou WebP.'
          )
        );
        return;
      }

      if (file.size > LIMITE_ARQUIVO) {
        reject(
          new Error(
            'A imagem original deve ter no máximo 5 MB.'
          )
        );
        return;
      }

      const imagem = new Image();
      const endereco =
        URL.createObjectURL(file);

      const tentativas = [
        { limite: 1200, qualidade: 0.88 },
        { limite: 1000, qualidade: 0.82 },
        { limite: 800, qualidade: 0.76 },
        { limite: 640, qualidade: 0.72 }
      ];

      imagem.onload = () => {
        try {
          let resultado = null;

          for (const tentativa of tentativas) {
            const escala = Math.min(
              1,
              tentativa.limite / imagem.width,
              tentativa.limite / imagem.height
            );

            const largura = Math.max(
              1,
              Math.round(imagem.width * escala)
            );

            const altura = Math.max(
              1,
              Math.round(imagem.height * escala)
            );

            const canvas =
              document.createElement('canvas');

            canvas.width = largura;
            canvas.height = altura;

            const contexto =
              canvas.getContext('2d');

            contexto.imageSmoothingEnabled = true;
            contexto.imageSmoothingQuality =
              'high';

            contexto.drawImage(
              imagem,
              0,
              0,
              largura,
              altura
            );

            const convertido =
              canvas.toDataURL(
                'image/webp',
                tentativa.qualidade
              );

            const tamanho =
              new Blob([convertido]).size;

            if (tamanho <= LIMITE_ARMAZENADO) {
              resultado = convertido;
              break;
            }
          }

          URL.revokeObjectURL(endereco);

          if (!resultado) {
            reject(
              new Error(
                'Não foi possível otimizar esta imagem. Tente uma imagem com dimensões menores.'
              )
            );
            return;
          }

          setCompanyLogo(resultado);
          resolve(resultado);
        } catch (error) {
          URL.revokeObjectURL(endereco);
          reject(error);
        }
      };

      imagem.onerror = () => {
        URL.revokeObjectURL(endereco);

        reject(
          new Error(
            'Não foi possível abrir a imagem selecionada.'
          )
        );
      };

      imagem.src = endereco;
    });
  };

  const value = {
    companyName,
    setCompanyName,
    companyLogo,
    setCompanyLogo,
    handleLogoUpload,
    instagramUsername,
    setInstagramUsername,
    instagramPassword,
    setInstagramPassword,
    darkMode,
    setDarkMode,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
